/**
 * Promo UI + delivery modal + physical 2-front / 1-back quad carousel.
 */
(() => {
  const FR_DAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const FR_MONTHS = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
  ];

  function isWeekend(d) {
    const day = d.getDay();
    return day === 0 || day === 6;
  }

  function addBusinessDays(date, days) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let left = days;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      if (!isWeekend(d)) left -= 1;
    }
    return d;
  }

  function estimateDeliveryDate(from = new Date()) {
    let ship = addBusinessDays(from, 1);
    if (ship.getDay() === 5) ship = addBusinessDays(ship, 1);
    while (isWeekend(ship)) ship = addBusinessDays(ship, 1);
    return addBusinessDays(ship, 2);
  }

  function formatFrDate(d) {
    return `${FR_DAYS[d.getDay()]} ${d.getDate()} ${FR_MONTHS[d.getMonth()]}`;
  }

  function initDeliveryEstimates() {
    document.querySelectorAll('[data-gal-delivery-estimate]').forEach((el) => {
      const dateEl = el.querySelector('[data-gal-delivery-date]');
      if (!dateEl) return;
      dateEl.textContent = ` : ${formatFrDate(estimateDeliveryDate())}`;
    });
  }

  function trapFocus(dialog) {
    const focusables = [...dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )].filter((n) => !n.hasAttribute('disabled'));
    if (!focusables.length) return () => {};
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    function onKey(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    dialog.addEventListener('keydown', onKey);
    return () => dialog.removeEventListener('keydown', onKey);
  }

  function initDeliveryModals() {
    document.querySelectorAll('[data-gal-delivery-open]').forEach((btn) => {
      const root = btn.closest('[data-gal-product]') || btn.closest('section') || document;
      const dialog =
        root.querySelector('[data-gal-delivery-modal]') ||
        document.querySelector('[data-gal-delivery-modal]');
      if (!dialog) return;

      let releaseTrap = null;
      const open = () => {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        releaseTrap = trapFocus(dialog);
        dialog.querySelector('[data-gal-delivery-close]')?.focus();
      };
      const close = () => {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
        releaseTrap?.();
        releaseTrap = null;
        btn.focus();
      };

      btn.addEventListener('click', open);
      dialog.querySelectorAll('[data-gal-delivery-close]').forEach((c) =>
        c.addEventListener('click', close)
      );
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) close();
      });
      dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        close();
      });
    });
  }

  /**
   * Physical stack carousel:
   * roles cycle: front-top / back-center / front-bottom
   * All 3 cards move together via CSS classes (transform/blur/opacity/z-index).
   */
  function initQuadAds(root) {
    const holdMs = Math.max(3000, Math.min(15000, Number(root.dataset.holdMs || 6000)));
    const animMs = Math.max(800, Math.min(950, Number(root.dataset.animMs || 850)));
    const autoplay = root.dataset.autoplay !== 'false';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rail = root.querySelector('[data-gal-quad-rail]') || root.querySelector('.gal-quad-ads__rail');
    const cards = [...root.querySelectorAll('[data-gal-quad-card]')];
    if (!rail || cards.length < 1) return;

    rail.style.setProperty('--gal-quad-anim', `${animMs}ms`);

    // Role order for N cards (1–3). With 3: top, back, bottom.
    const ROLES =
      cards.length >= 3
        ? ['is-front-top', 'is-back-center', 'is-front-bottom']
        : cards.length === 2
          ? ['is-front-top', 'is-front-bottom']
          : ['is-front-top'];

    let offset = 0; // which card index is front-top
    let timer = null;
    let paused = false;
    let animating = false;

    const apply = () => {
      cards.forEach((card, i) => {
        const roleIndex = (i - offset + cards.length) % cards.length;
        const role = ROLES[Math.min(roleIndex, ROLES.length - 1)];
        card.classList.remove('is-front-top', 'is-front-bottom', 'is-back-center');
        // Map: roleIndex 0 = front-top, 1 = back-center, 2 = front-bottom
        if (cards.length >= 3) {
          if (roleIndex === 0) card.classList.add('is-front-top');
          else if (roleIndex === 1) card.classList.add('is-back-center');
          else card.classList.add('is-front-bottom');
        } else {
          card.classList.add(role);
        }
        const hidden = cards.length >= 3 && roleIndex === 1;
        card.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      });
    };

    const next = () => {
      if (animating || cards.length < 2) return;
      animating = true;
      offset = (offset + 1) % cards.length;
      apply();
      window.setTimeout(() => {
        animating = false;
      }, reduced ? 0 : animMs);
    };

    const prev = () => {
      if (animating || cards.length < 2) return;
      animating = true;
      offset = (offset - 1 + cards.length) % cards.length;
      apply();
      window.setTimeout(() => {
        animating = false;
      }, reduced ? 0 : animMs);
    };

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    const start = () => {
      stop();
      if (!autoplay || reduced || paused || cards.length < 2) return;
      timer = setInterval(next, holdMs + (reduced ? 0 : animMs));
    };

    const pauseZone = rail;
    pauseZone.addEventListener('mouseenter', () => {
      paused = true;
      stop();
    });
    pauseZone.addEventListener('mouseleave', () => {
      paused = false;
      start();
    });
    pauseZone.addEventListener('focusin', () => {
      paused = true;
      stop();
    });
    pauseZone.addEventListener('focusout', (e) => {
      if (!pauseZone.contains(e.relatedTarget)) {
        paused = false;
        start();
      }
    });

    root.querySelector('[data-gal-quad-prev]')?.addEventListener('click', () => {
      prev();
      start();
    });
    root.querySelector('[data-gal-quad-next]')?.addEventListener('click', () => {
      next();
      start();
    });

    apply();
    start();
  }

  document.addEventListener('DOMContentLoaded', () => {
    initDeliveryEstimates();
    initDeliveryModals();
    document.querySelectorAll('[data-gal-quad-ads]').forEach(initQuadAds);
  });
})();
